<template>
  <div v-if="show" class="column-selector-popup">
    <div class="popup-overlay" @click="$emit('close')"></div>
    <div class="popup-content" :class="popupWidthClass">
      <div class="popup-header">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">{{ $t("Customize display") }}</h6>
          <div class="d-flex align-items-center">
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="select-all-columns"
                :checked="areAllTempColumnsSelected"
                @change="handleToggleAllColumns"
              />
              <label
                class="form-check-label select-all-label"
                for="select-all-columns"
              >
                {{ $t("Select all") }}
              </label>
            </div>
            <button class="btn-close" @click="$emit('close')">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="column-count mt-2">
          <span class="count-badge">
            <i class="fas fa-check me-1"></i>
            {{ $t("columnSelectorSelectedCount", { count: selectedColumnsCount }) }}
          </span>
        </div>
        <div class="search-box mt-2">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model.trim="searchQuery"
            type="text"
            class="form-control form-control-sm"
            :placeholder="$t('Search fields...')"
          />
        </div>
      </div>

      <div class="popup-body">
        <!-- Campos simples (no reference, no group) -->
        <div
          v-for="field in filteredSimpleFields"
          :key="field.key"
          class="column-item"
        >
          <div class="column-toggle">
            <input
              class="form-check-input"
              type="checkbox"
              :id="'col-' + field.key"
              v-model="tempSelectedColumns[field.key]"
            />
            <label class="form-check-label" :for="'col-' + field.key">
              {{ $t(field.label) }}
              <span class="badge badge-warning" style="font-size: 0.6rem; margin-left: 4px;">
                {{field.type}}
              </span>
            </label>
            <!-- Checkbox Sum para campos numéricos que son arrays -->
            <div
              v-if="field.isArray && isNumericField(field.type)"
              class="form-check sum-checkbox"
              style="margin-left: 20px; margin-top: 4px;"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="'sum-' + field.key"
                :checked="getColumnSum(field.key)"
                @change="toggleColumnSum(field.key, $event.target.checked)"
                :disabled="!tempSelectedColumns[field.key]"
              />
              <label
                class="form-check-label"
                :for="'sum-' + field.key"
                style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                :title="$t('Sum values')"
              >
                Sum
              </label>
            </div>
          </div>
          <div class="column-handle">
            <i class="fas fa-grip-vertical"></i>
          </div>
        </div>

        <!-- Campos tipo reference (expandibles) -->
        <div
          v-for="field in filteredReferenceFields"
          :key="field.key"
          class="group-section reference-section"
        >
          <div class="column-item">
            <div class="column-toggle">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'ref-' + field.key"
                v-model="tempSelectedColumns[field.key]"
              />
              <label class="form-check-label" :for="'ref-' + field.key">
                {{ $t(field.label) }}
                <span class="badge badge-info" style="font-size: 0.6rem; margin-left: 4px;">
                  reference
                </span>
              </label>
            </div>
            <div class="column-actions">
              <button 
                class="btn-expand"
                @click="toggleReferenceExpand(field)"
                :title="expandedReferences[field.key] ? 'Collapse' : 'Expand to see nested fields'"
              >
                <i :class="expandedReferences[field.key] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </button>
            </div>
          </div>

          <!-- Subcampos del reference (cuando está expandido) -->
          <div v-if="expandedReferences[field.key]" class="sub-columns reference-sub-columns">
            <div v-if="loadingReferenceFields[field.key]" class="loading-indicator">
              <i class="fas fa-spinner fa-spin"></i> Loading fields...
            </div>
            <div
              v-else-if="getFilteredReferenceSubFields(field.key).length > 0"
              v-for="subField in getFilteredReferenceSubFields(field.key)"
              :key="subField.key"
              class="column-item sub-item"
            >
              <div class="column-toggle">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="'col-' + field.key + '-' + subField.key"
                  v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                />
                <label
                  class="form-check-label"
                  :for="'col-' + field.key + '-' + subField.key"
                >
                  {{ $t(subField.label) }}
                  <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                    {{subField.type}}
                  </span>
                </label>
                <!-- Checkbox Sum para campos numéricos en reference arrays -->
                <div
                  v-if="field.isArray && isNumericField(subField.type)"
                  class="form-check sum-checkbox"
                  style="margin-left: 20px; margin-top: 4px;"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'sum-' + field.key + '-' + subField.key"
                    :checked="getColumnSum(`${field.key}.${subField.key}`)"
                    @change="toggleColumnSum(`${field.key}.${subField.key}`, $event.target.checked)"
                    :disabled="!tempSelectedColumns[`${field.key}.${subField.key}`]"
                  />
                  <label
                    class="form-check-label"
                    :for="'sum-' + field.key + '-' + subField.key"
                    style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                    :title="$t('Sum values')"
                  >
                    Sum
                  </label>
                </div>
              </div>
            </div>
            <div v-else class="no-fields-message">
              {{ $t("columnSelectorNoFields") }}
            </div>
          </div>
        </div>

        <!-- Campos tipo grupo -->
        <div
          v-for="field in filteredGroupFields"
          :key="field.key"
          class="group-section"
        >
          <div class="column-item">
            <div class="column-toggle">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'group-' + field.key"
                v-model="tempSelectedColumns[field.key]"
                @change="toggleGroupFields(field, field.key)"
              />
              <label class="form-check-label" :for="'group-' + field.key">
                <strong>{{ $t(field.label) }}</strong>
              </label>
            </div>
            <div class="column-handle">
              <i class="fas fa-grip-vertical"></i>
            </div>
          </div>

          <div class="sub-columns">
            <template v-for="subField in sortedSubFields(field.fields)">
              <!-- Si el subcampo es un grupo, renderizar recursivamente -->
              <div
                v-if="subField.type === 'group'"
                :key="'nested-group-' + subField.key"
                class="nested-group-section"
              >
                <div class="column-item sub-item nested-group-header">
                  <div class="column-toggle">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'col-' + field.key + '-' + subField.key"
                      v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                      @change="toggleNestedGroupFields(subField, `${field.key}.${subField.key}`, field)"
                    />
                    <label
                      class="form-check-label"
                      :for="'col-' + field.key + '-' + subField.key"
                    >
                      <strong>{{ $t(subField.label) }}</strong>
                      <span class="badge badge-primary" style="font-size: 0.6rem; margin-left: 4px;">
                        group
                      </span>
                    </label>
                  </div>
                  <div class="column-handle">
                    <i class="fas fa-grip-vertical"></i>
                  </div>
                </div>
                
                <!-- Campos dentro del grupo anidado (2do nivel) -->
                <div class="sub-columns nested-level-2">
                  <template v-for="nestedField in sortedSubFields(subField.fields || [])">
                    <!-- Si hay un 3er nivel de grupo -->
                    <div
                      v-if="nestedField.type === 'group'"
                      :key="'deep-group-' + nestedField.key"
                      class="nested-group-section level-3"
                    >
                      <div class="column-item sub-item nested-group-header">
                        <div class="column-toggle">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                            v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}`]"
                            @change="toggleDeepNestedGroupFields(nestedField, `${field.key}.${subField.key}.${nestedField.key}`, subField, field)"
                          />
                          <label
                            class="form-check-label"
                            :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                          >
                            <strong>{{ $t(nestedField.label) }}</strong>
                            <span class="badge badge-primary" style="font-size: 0.6rem; margin-left: 4px;">
                              group
                            </span>
                          </label>
                        </div>
                      </div>
                      
                      <!-- Campos dentro del grupo de 3er nivel -->
                      <div class="sub-columns nested-level-3">
                        <div
                          v-for="deepField in sortedSubFields(nestedField.fields || [])"
                          :key="'deep-iter-' + deepField.key"
                          class="nested-level-3-field-wrap"
                        >
                          <!-- Reference bajo grupo isArray anidado (ruta ...grupo.ref.subcampo) -->
                          <div
                            v-if="deepField.type === 'reference' && deepField.ref && deepField.ref !== 'users' && field.isArray && isArrayGroup(subField) && isArrayGroup(nestedField)"
                            class="group-section reference-section sub-item"
                          >
                            <div class="column-item sub-item">
                              <div class="column-toggle">
                                <input
                                  class="form-check-input"
                                  type="checkbox"
                                  :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                                  v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}`]"
                                  @change="updateDeepGroupSelection(nestedField, `${field.key}.${subField.key}.${nestedField.key}`, subField, `${field.key}.${subField.key}`, field)"
                                />
                                <label
                                  class="form-check-label"
                                  :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                                >
                                  {{ $t(deepField.label) }}
                                  <span class="badge badge-info" style="font-size: 0.6rem; margin-left: 4px;">
                                    reference
                                  </span>
                                </label>
                              </div>
                              <div class="column-actions">
                                <button
                                  type="button"
                                  class="btn-expand"
                                  @click="toggleGarRefExpand(deepField, garRefStorageKey(field, subField, nestedField, deepField))"
                                  :title="expandedGarRefs[garRefStorageKey(field, subField, nestedField, deepField)] ? 'Collapse' : 'Expand to see nested fields'"
                                >
                                  <i :class="expandedGarRefs[garRefStorageKey(field, subField, nestedField, deepField)] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                </button>
                              </div>
                            </div>
                            <div
                              v-if="expandedGarRefs[garRefStorageKey(field, subField, nestedField, deepField)]"
                              class="sub-columns reference-sub-columns"
                            >
                              <div v-if="loadingGarRefFields[garRefStorageKey(field, subField, nestedField, deepField)]" class="loading-indicator">
                                <i class="fas fa-spinner fa-spin"></i> Loading fields...
                              </div>
                              <div
                                v-else-if="getFilteredGarRefSubFields(garRefStorageKey(field, subField, nestedField, deepField), garRefStorageKey(field, subField, nestedField, deepField)).length > 0"
                                v-for="gSub in getFilteredGarRefSubFields(garRefStorageKey(field, subField, nestedField, deepField), garRefStorageKey(field, subField, nestedField, deepField))"
                                :key="'gar-deep-sf-' + deepField.key + '-' + gSub.key"
                                class="column-item sub-item"
                              >
                                <div class="column-toggle">
                                  <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key + '-' + gSub.key"
                                    v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}.${gSub.key}`]"
                                    @click.stop
                                    @change="updateDeepGroupSelection(nestedField, `${field.key}.${subField.key}.${nestedField.key}`, subField, `${field.key}.${subField.key}`, field)"
                                  />
                                  <label
                                    class="form-check-label"
                                    :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key + '-' + gSub.key"
                                  >
                                    {{ $t(gSub.label) }}
                                    <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                                      {{ gSub.type }}
                                    </span>
                                  </label>
                                  <div
                                    v-if="field.isArray && isNumericField(gSub.type)"
                                    class="form-check sum-checkbox"
                                    style="margin-left: 20px; margin-top: 4px;"
                                  >
                                    <input
                                      class="form-check-input"
                                      type="checkbox"
                                      :id="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key + '-' + gSub.key"
                                      :checked="getColumnSum(`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}.${gSub.key}`)"
                                      @change="toggleColumnSum(`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}.${gSub.key}`, $event.target.checked)"
                                      :disabled="!tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}.${gSub.key}`]"
                                    />
                                    <label
                                      class="form-check-label"
                                      :for="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key + '-' + gSub.key"
                                      style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                                      :title="$t('Sum values')"
                                    >
                                      Sum
                                    </label>
                                  </div>
                                </div>
                              </div>
                              <div v-else-if="!loadingGarRefFields[garRefStorageKey(field, subField, nestedField, deepField)]" class="no-fields-message">
                                {{ $t("columnSelectorNoFields") }}
                              </div>
                            </div>
                          </div>
                          <div
                            v-else
                            class="column-item sub-item"
                          >
                            <div class="column-toggle">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                                v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}`]"
                                @change="updateDeepGroupSelection(nestedField, `${field.key}.${subField.key}.${nestedField.key}`, subField, `${field.key}.${subField.key}`, field)"
                              />
                              <label
                                class="form-check-label"
                                :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                              >
                                {{ $t(deepField.label) }}
                                <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                                  {{deepField.type}}
                                </span>
                              </label>
                              <!-- Checkbox Sum para campos numéricos en grupos múltiples de 3er nivel -->
                              <div
                                v-if="field.isArray && isNumericField(deepField.type)"
                                class="form-check sum-checkbox"
                                style="margin-left: 20px; margin-top: 4px;"
                              >
                                <input
                                  class="form-check-input"
                                  type="checkbox"
                                  :id="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                                  :checked="getColumnSum(`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}`)"
                                  @change="toggleColumnSum(`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}`, $event.target.checked)"
                                  :disabled="!tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}`]"
                                />
                                <label
                                  class="form-check-label"
                                  :for="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                                  style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                                  :title="$t('Sum values')"
                                >
                                  Sum
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Reference bajo grupo isArray (una jerarquía de grupo anidado) -->
                    <div
                      v-else-if="nestedField.type === 'reference' && nestedField.ref && nestedField.ref !== 'users' && field.isArray && isArrayGroup(subField)"
                      :key="'gar-nested-ref-' + nestedField.key"
                      class="group-section reference-section sub-item nested-level-2"
                    >
                      <div class="column-item sub-item">
                        <div class="column-toggle">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                            v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}`]"
                            @change="updateNestedGroupSelection(subField, `${field.key}.${subField.key}`, field)"
                          />
                          <label
                            class="form-check-label"
                            :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                          >
                            {{ $t(nestedField.label) }}
                            <span class="badge badge-info" style="font-size: 0.6rem; margin-left: 4px;">
                              reference
                            </span>
                          </label>
                        </div>
                        <div class="column-actions">
                          <button
                            type="button"
                            class="btn-expand"
                            @click="toggleGarRefExpand(nestedField, garRefStorageKey(field, subField, nestedField))"
                            :title="expandedGarRefs[garRefStorageKey(field, subField, nestedField)] ? 'Collapse' : 'Expand to see nested fields'"
                          >
                            <i :class="expandedGarRefs[garRefStorageKey(field, subField, nestedField)] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="expandedGarRefs[garRefStorageKey(field, subField, nestedField)]"
                        class="sub-columns reference-sub-columns"
                      >
                        <div v-if="loadingGarRefFields[garRefStorageKey(field, subField, nestedField)]" class="loading-indicator">
                          <i class="fas fa-spinner fa-spin"></i> Loading fields...
                        </div>
                        <div
                          v-else-if="getFilteredGarRefSubFields(garRefStorageKey(field, subField, nestedField), garRefStorageKey(field, subField, nestedField)).length > 0"
                          v-for="gSub in getFilteredGarRefSubFields(garRefStorageKey(field, subField, nestedField), garRefStorageKey(field, subField, nestedField))"
                          :key="'gar-nested-sf-' + nestedField.key + '-' + gSub.key"
                          class="column-item sub-item"
                        >
                          <div class="column-toggle">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + gSub.key"
                              v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${gSub.key}`]"
                              @click.stop
                              @change="updateNestedGroupSelection(subField, `${field.key}.${subField.key}`, field)"
                            />
                            <label
                              class="form-check-label"
                              :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + gSub.key"
                            >
                              {{ $t(gSub.label) }}
                              <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                                {{ gSub.type }}
                              </span>
                            </label>
                            <div
                              v-if="field.isArray && isNumericField(gSub.type)"
                              class="form-check sum-checkbox"
                              style="margin-left: 20px; margin-top: 4px;"
                            >
                              <input
                                class="form-check-input"
                                type="checkbox"
                                :id="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + gSub.key"
                                :checked="getColumnSum(`${field.key}.${subField.key}.${nestedField.key}.${gSub.key}`)"
                                @change="toggleColumnSum(`${field.key}.${subField.key}.${nestedField.key}.${gSub.key}`, $event.target.checked)"
                                :disabled="!tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${gSub.key}`]"
                              />
                              <label
                                class="form-check-label"
                                :for="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + gSub.key"
                                style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                                :title="$t('Sum values')"
                              >
                                Sum
                              </label>
                            </div>
                          </div>
                        </div>
                        <div v-else-if="!loadingGarRefFields[garRefStorageKey(field, subField, nestedField)]" class="no-fields-message">
                          {{ $t("columnSelectorNoFields") }}
                        </div>
                      </div>
                    </div>
                    <!-- Campo normal dentro del grupo anidado -->
                    <div
                      v-else
                      :key="'nested-field-' + nestedField.key"
                      class="column-item sub-item"
                    >
                      <div class="column-toggle">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="'col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                          v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}`]"
                          @change="updateNestedGroupSelection(subField, `${field.key}.${subField.key}`, field)"
                        />
                        <label
                          class="form-check-label"
                          :for="'col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                        >
                          {{ $t(nestedField.label) }}
                          <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                            {{nestedField.type}}
                          </span>
                        </label>
                        <!-- Checkbox Sum para campos numéricos en grupos múltiples anidados -->
                        <div
                          v-if="field.isArray && isNumericField(nestedField.type)"
                          class="form-check sum-checkbox"
                          style="margin-left: 20px; margin-top: 4px;"
                        >
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key"
                            :checked="getColumnSum(`${field.key}.${subField.key}.${nestedField.key}`)"
                            @change="toggleColumnSum(`${field.key}.${subField.key}.${nestedField.key}`, $event.target.checked)"
                            :disabled="!tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}`]"
                          />
                          <label
                            class="form-check-label"
                            :for="'sum-' + field.key + '-' + subField.key + '-' + nestedField.key"
                            style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                            :title="$t('Sum values')"
                          >
                            Sum
                          </label>
                        </div>
                      </div>
                      <div class="column-handle">
                        <i class="fas fa-grip-vertical"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              
              <!-- Reference directo bajo grupo isArray (grupo.ref.subcampo) -->
              <div
                v-else-if="subField.type === 'reference' && subField.ref && subField.ref !== 'users' && field.isArray"
                :key="'gar-root-ref-' + subField.key"
                class="group-section reference-section sub-item"
              >
                <div class="column-item sub-item">
                  <div class="column-toggle">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'col-' + field.key + '-' + subField.key"
                      v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                      @change="updateGroupSelection(field)"
                    />
                    <label
                      class="form-check-label"
                      :for="'col-' + field.key + '-' + subField.key"
                    >
                      {{ $t(subField.label) }}
                      <span class="badge badge-info" style="font-size: 0.6rem; margin-left: 4px;">
                        reference
                      </span>
                    </label>
                  </div>
                  <div class="column-actions">
                    <button
                      type="button"
                      class="btn-expand"
                      @click="toggleGarRefExpand(subField, garRefStorageKey(field, subField))"
                      :title="expandedGarRefs[garRefStorageKey(field, subField)] ? 'Collapse' : 'Expand to see nested fields'"
                    >
                      <i :class="expandedGarRefs[garRefStorageKey(field, subField)] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                    </button>
                  </div>
                </div>
                <div
                  v-if="expandedGarRefs[garRefStorageKey(field, subField)]"
                  class="sub-columns reference-sub-columns"
                >
                  <div v-if="loadingGarRefFields[garRefStorageKey(field, subField)]" class="loading-indicator">
                    <i class="fas fa-spinner fa-spin"></i> Loading fields...
                  </div>
                  <div
                    v-else-if="getFilteredGarRefSubFields(garRefStorageKey(field, subField), garRefStorageKey(field, subField)).length > 0"
                    v-for="gSub in getFilteredGarRefSubFields(garRefStorageKey(field, subField), garRefStorageKey(field, subField))"
                    :key="'gar-root-sf-' + subField.key + '-' + gSub.key"
                    class="column-item sub-item"
                    :class="{ 'reference-section': gSub.type === 'reference' && gSub.ref && gSub.ref !== 'users' }"
                  >
                    <div class="column-toggle" style="display: flex; align-items: center; width: 100%;">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'col-' + field.key + '-' + subField.key + '-' + gSub.key"
                        v-model="tempSelectedColumns[`${field.key}.${subField.key}.${gSub.key}`]"
                        @click.stop
                        @change="updateGroupSelection(field)"
                      />
                      <label
                        class="form-check-label"
                        :for="'col-' + field.key + '-' + subField.key + '-' + gSub.key"
                      >
                        {{ $t(gSub.label) }}
                        <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                          {{ gSub.type }}
                        </span>
                      </label>
                      <button
                        v-if="gSub.type === 'reference' && gSub.ref && gSub.ref !== 'users'"
                        type="button"
                        class="btn-expand"
                        style="margin-left: auto;"
                        @click="toggleGarRefExpand(gSub, garRefStorageKey(field, subField, gSub))"
                        :title="expandedGarRefs[garRefStorageKey(field, subField, gSub)] ? 'Collapse' : 'Expand to see nested fields'"
                      >
                        <i :class="expandedGarRefs[garRefStorageKey(field, subField, gSub)] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                      </button>
                      <div
                        v-if="field.isArray && isNumericField(gSub.type)"
                        class="form-check sum-checkbox"
                        style="margin-left: 20px; margin-top: 4px;"
                      >
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="'sum-' + field.key + '-' + subField.key + '-' + gSub.key"
                          :checked="getColumnSum(`${field.key}.${subField.key}.${gSub.key}`)"
                          @change="toggleColumnSum(`${field.key}.${subField.key}.${gSub.key}`, $event.target.checked)"
                          :disabled="!tempSelectedColumns[`${field.key}.${subField.key}.${gSub.key}`]"
                        />
                        <label
                          class="form-check-label"
                          :for="'sum-' + field.key + '-' + subField.key + '-' + gSub.key"
                          style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                          :title="$t('Sum values')"
                        >
                          Sum
                        </label>
                      </div>
                    </div>
                    <div
                      v-if="gSub.type === 'reference' && gSub.ref && gSub.ref !== 'users' && expandedGarRefs[garRefStorageKey(field, subField, gSub)]"
                      class="sub-columns reference-sub-columns"
                    >
                      <div v-if="loadingGarRefFields[garRefStorageKey(field, subField, gSub)]" class="loading-indicator">
                        <i class="fas fa-spinner fa-spin"></i> Loading fields...
                      </div>
                      <div
                        v-else-if="getFilteredGarRefSubFields(garRefStorageKey(field, subField, gSub), garRefStorageKey(field, subField, gSub)).length > 0"
                        v-for="gDeep in getFilteredGarRefSubFields(garRefStorageKey(field, subField, gSub), garRefStorageKey(field, subField, gSub))"
                        :key="'gar-root-deep-' + subField.key + '-' + gSub.key + '-' + gDeep.key"
                        class="column-item sub-item"
                      >
                        <div class="column-toggle">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'col-' + field.key + '-' + subField.key + '-' + gSub.key + '-' + gDeep.key"
                            v-model="tempSelectedColumns[`${field.key}.${subField.key}.${gSub.key}.${gDeep.key}`]"
                            @click.stop
                            @change="updateGroupSelection(field)"
                          />
                          <label
                            class="form-check-label"
                            :for="'col-' + field.key + '-' + subField.key + '-' + gSub.key + '-' + gDeep.key"
                          >
                            {{ $t(gDeep.label) }}
                            <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                              {{ gDeep.type }}
                            </span>
                          </label>
                          <div
                            v-if="field.isArray && isNumericField(gDeep.type)"
                            class="form-check sum-checkbox"
                            style="margin-left: 20px; margin-top: 4px;"
                          >
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="'sum-' + field.key + '-' + subField.key + '-' + gSub.key + '-' + gDeep.key"
                              :checked="getColumnSum(`${field.key}.${subField.key}.${gSub.key}.${gDeep.key}`)"
                              @change="toggleColumnSum(`${field.key}.${subField.key}.${gSub.key}.${gDeep.key}`, $event.target.checked)"
                              :disabled="!tempSelectedColumns[`${field.key}.${subField.key}.${gSub.key}.${gDeep.key}`]"
                            />
                            <label
                              class="form-check-label"
                              :for="'sum-' + field.key + '-' + subField.key + '-' + gSub.key + '-' + gDeep.key"
                              style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                              :title="$t('Sum values')"
                            >
                              Sum
                            </label>
                          </div>
                        </div>
                      </div>
                      <div v-else-if="!loadingGarRefFields[garRefStorageKey(field, subField, gSub)]" class="no-fields-message">
                        {{ $t("columnSelectorNoFields") }}
                      </div>
                    </div>
                  </div>
                  <div v-else-if="!loadingGarRefFields[garRefStorageKey(field, subField)]" class="no-fields-message">
                    {{ $t("columnSelectorNoFields") }}
                  </div>
                </div>
              </div>
              <!-- Campo normal (no grupo) -->
              <div
                v-else
                :key="'field-' + subField.key"
                class="column-item sub-item"
              >
                <div class="column-toggle">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'col-' + field.key + '-' + subField.key"
                    v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                    @change="updateGroupSelection(field)"
                  />
                  <label
                    class="form-check-label"
                    :for="'col-' + field.key + '-' + subField.key"
                  >
                    {{ $t(subField.label) }}
                    <span class="badge badge-warning" style="font-size: 0.6rem; margin-left: 4px;">
                      {{subField.type}}
                    </span>
                  </label>
                  <!-- Checkbox Sum para campos numéricos en grupos múltiples -->
                  <div
                    v-if="field.isArray && isNumericField(subField.type)"
                    class="form-check sum-checkbox"
                    style="margin-left: 20px; margin-top: 4px;"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'sum-' + field.key + '-' + subField.key"
                      :checked="getColumnSum(`${field.key}.${subField.key}`)"
                      @change="toggleColumnSum(`${field.key}.${subField.key}`, $event.target.checked)"
                      :disabled="!tempSelectedColumns[`${field.key}.${subField.key}`]"
                    />
                    <label
                      class="form-check-label"
                      :for="'sum-' + field.key + '-' + subField.key"
                      style="font-size: 0.75rem; color: #6c757d; cursor: pointer;"
                      :title="$t('Sum values')"
                    >
                      Sum
                    </label>
                  </div>
                </div>
                <div class="column-handle">
                  <i class="fas fa-grip-vertical"></i>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div v-if="!hasVisibleFields" class="no-fields-message">
          {{ $t('No fields found for "{query}"', { query: searchQuery }) }}
        </div>
      </div>

      <div class="popup-footer">
        <button class="btn btn-outline-secondary btn-sm" @click="cancelChanges">
          {{ $t("Cancel") }}
        </button>
        <button class="btn btn-primary btn-sm" @click="applyChanges">
          {{ $t("Apply") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { withReferenceSystemSubFields } from "@/utils/referenceSystemSubFields";

export default {
  name: "ColumnSelector",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    tableFields: {
      type: Array,
      required: true,
    },
    selectedColumns: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tempSelectedColumns: {},
      tempColumnSums: {},
      expandedReferences: {},
      referenceSubFields: {},
      loadingReferenceFields: {},
      expandedGarRefs: {},
      garRefSubFields: {},
      loadingGarRefFields: {},
      searchQuery: "",
    };
  },
  watch: {
    show: {
      handler(newVal) {
        if (newVal) {
          this.searchQuery = "";
          // Inicializar columnas temporales cuando se abre el popup
          this.tempSelectedColumns = JSON.parse(
            JSON.stringify(this.selectedColumns)
          );
          // Inicializar sumas temporales si existen
          if (this.selectedColumns._columnSums) {
            this.tempColumnSums = JSON.parse(
              JSON.stringify(this.selectedColumns._columnSums)
            );
          } else {
            this.tempColumnSums = {};
          }
        }
      },
      immediate: true,
    },
    selectedColumns: {
      handler(newVal) {
        // Forzar la actualización del componente cuando cambian las columnas seleccionadas
        this.$forceUpdate();
      },
      deep: true,
    },
  },
  computed: {
    // Profundidad de jerarquias abiertas: 0 = nada expandido,
    // 1 = un reference de primer nivel, 2+ = references anidados
    expandedDepth() {
      let depth = 0;
      if (Object.keys(this.expandedReferences).some((key) => this.expandedReferences[key])) {
        depth = 1;
      }
      Object.keys(this.expandedGarRefs).forEach((storageKey) => {
        if (!this.expandedGarRefs[storageKey]) return;
        depth = Math.max(depth, String(storageKey).split(".").length);
      });
      return depth;
    },
    // El popup se ensancha a medida que se abren mas niveles, para que la
    // sangria acumulada no aplaste los nombres de los campos
    popupWidthClass() {
      const depth = this.expandedDepth;
      if (depth >= 4) return "popup-width-xl";
      if (depth === 3) return "popup-width-lg";
      if (depth === 2) return "popup-width-md";
      if (depth === 1) return "popup-width-sm";
      return "";
    },
    normalizedSearchTerm() {
      return this.normalizeText(this.searchQuery);
    },
    // Campos simples (no reference expandible, no group) - incluye references a users
    simpleFields() {
      return this.tableFields
        .filter((field) => {
          if (field.hideDisplay) return false;
          if (field.type === "group") return false;
          // Referencias a users van en campos simples (sin opción de expandir)
          if (field.type === "reference" && field.ref === "users") return true;
          // Otras referencias van en su propia sección (expandibles)
          if (field.type === "reference") return false;
          return true;
        })
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    filteredSimpleFields() {
      if (!this.normalizedSearchTerm) return this.simpleFields;
      return this.simpleFields.filter((field) => this.fieldMatchesSearch(field));
    },
    // Campos de tipo reference (expandibles) - excluye referencias a users
    referenceFields() {
      return this.tableFields
        .filter((field) => field.type === "reference" && !field.hideDisplay && field.ref && field.ref !== "users")
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    filteredReferenceFields() {
      if (!this.normalizedSearchTerm) return this.referenceFields;
      return this.referenceFields.filter((field) => {
        if (this.fieldMatchesSearch(field)) return true;
        return this.getFilteredReferenceSubFields(field.key).length > 0;
      });
    },
    nonGroupFields() {
      return this.tableFields
        .filter((field) => field.type !== "group" && !field.hideDisplay)
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    groupFields() {
      return this.tableFields
        .filter((field) => field.type === "group" && !field.hideDisplay)
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    filteredGroupFields() {
      if (!this.normalizedSearchTerm) return this.groupFields;
      return this.groupFields
        .map((field) => this.filterGroupFieldForSearch(field))
        .filter((field) => Boolean(field));
    },
    hasVisibleFields() {
      return (
        this.filteredSimpleFields.length > 0 ||
        this.filteredReferenceFields.length > 0 ||
        this.filteredGroupFields.length > 0
      );
    },
    areAllTempColumnsSelected() {
      // Verificar si todos los campos normales están seleccionados
      const allNormalFieldsSelected = this.nonGroupFields.every(
        (field) => this.tempSelectedColumns[field.key] === true
      );

      // Función recursiva para verificar campos anidados
      const allNestedSelected = (fields, parentPath) => {
        return fields.every((subField) => {
          if (subField.hideDisplay) return true;
          const fieldPath = `${parentPath}.${subField.key}`;
          
          if (this.tempSelectedColumns[fieldPath] !== true) {
            return false;
          }
          
          // Verificar campos anidados si es un grupo
          if (subField.type === 'group' && subField.fields) {
            return allNestedSelected(subField.fields, fieldPath);
          }
          
          return true;
        });
      };

      // Verificar si todos los campos de grupo están seleccionados recursivamente
      const allGroupFieldsSelected = this.groupFields.every((groupField) => {
        if (this.tempSelectedColumns[groupField.key] !== true) {
          return false;
        }
        return allNestedSelected(groupField.fields || [], groupField.key);
      });

      return allNormalFieldsSelected && allGroupFieldsSelected;
    },
    selectedColumnsCount() {
      let count = 0;

      // Contar campos simples seleccionados
      this.simpleFields.forEach((field) => {
        if (this.tempSelectedColumns[field.key] === true) {
          count++;
        }
      });

      // Contar campos de referencia y sus subcampos seleccionados
      this.referenceFields.forEach((field) => {
        if (this.tempSelectedColumns[field.key] === true) {
          count++;
        }
        // Contar subcampos de reference seleccionados
        const subFields = this.referenceSubFields[field.key] || [];
        subFields.forEach((subField) => {
          if (this.tempSelectedColumns[`${field.key}.${subField.key}`] === true) {
            count++;
          }
        });
      });

      // Función recursiva para contar campos anidados
      const countNested = (fields, parentPath) => {
        let nestedCount = 0;
        fields.forEach((subField) => {
          if (!subField.hideDisplay) {
            const fieldPath = `${parentPath}.${subField.key}`;
            if (this.tempSelectedColumns[fieldPath] === true) {
              nestedCount++;
            }
            // Contar campos anidados si es un grupo
            if (subField.type === 'group' && subField.fields) {
              nestedCount += countNested(subField.fields, fieldPath);
            }
          }
        });
        return nestedCount;
      };

      // Contar campos de grupo y subcampos seleccionados recursivamente
      this.groupFields.forEach((field) => {
        if (this.tempSelectedColumns[field.key] === true) {
          count++;
        }
        count += countNested(field.fields || [], field.key);
      });

      return count;
    },
  },
  methods: {
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    fieldMatchesSearch(field, pathPrefix = "") {
      const term = this.normalizedSearchTerm;
      if (!term) return true;
      const path = pathPrefix ? `${pathPrefix}.${field.key}` : field.key;
      const searchableText = [
        this.$t(field.label || ""),
        field.label || "",
        field.key || "",
        field.type || "",
        path,
      ]
        .map((v) => this.normalizeText(v))
        .join(" ");
      return searchableText.includes(term);
    },
    getFilteredReferenceSubFields(referenceKey) {
      const subFields = this.referenceSubFields[referenceKey] || [];
      if (!this.normalizedSearchTerm) return subFields;
      return subFields.filter((subField) =>
        this.fieldMatchesSearch(subField, referenceKey)
      );
    },
    getFilteredGarRefSubFields(storageKey, pathPrefix = "") {
      const subFields = this.garRefSubFields[storageKey] || [];
      if (!this.normalizedSearchTerm) return subFields;
      return subFields.filter((subField) =>
        this.fieldMatchesSearch(subField, pathPrefix)
      );
    },
    filterGroupFieldForSearch(field, parentPath = "") {
      const currentPath = parentPath ? `${parentPath}.${field.key}` : field.key;
      const selfMatches = this.fieldMatchesSearch(field, parentPath);

      if (!field.fields || field.fields.length === 0) {
        if (field.type === "reference" && field.ref && field.ref !== "users") {
          const storageKey = currentPath;
          return selfMatches || this.getFilteredGarRefSubFields(storageKey, storageKey).length > 0
            ? { ...field }
            : null;
        }
        return selfMatches ? { ...field } : null;
      }

      const filteredChildren = field.fields
        .map((subField) => this.filterGroupFieldForSearch(subField, currentPath))
        .filter((subField) => Boolean(subField));

      if (selfMatches || filteredChildren.length > 0) {
        return {
          ...field,
          fields: filteredChildren,
        };
      }
      return null;
    },
    sortedSubFields(fields) {
      return fields
        .filter((sf) => !sf.hideDisplay)
        .sort((a, b) => {
          // Primero los grupos, luego el resto alfabéticamente
          if (a.type === 'group' && b.type !== 'group') return -1;
          if (a.type !== 'group' && b.type === 'group') return 1;
          // Si ambos son grupos o ambos no son grupos, ordenar alfabéticamente
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    applyChanges() {
      // Copiar las columnas temporales a las reales (omitir meta keys)
      Object.keys(this.tempSelectedColumns).forEach((key) => {
        if (
          key === "_columnSums" ||
          key === "_clipViewMore" ||
          key === "_wrapText"
        ) {
          return;
        }
        this.$set(this.selectedColumns, key, this.tempSelectedColumns[key]);
      });

      // Copiar las sumas temporales a las reales
      if (!this.selectedColumns._columnSums) {
        this.$set(this.selectedColumns, '_columnSums', {});
      }
      Object.keys(this.tempColumnSums).forEach((key) => {
        this.$set(this.selectedColumns._columnSums, key, this.tempColumnSums[key]);
      });

      // Preservar metas _clipViewMore y _wrapText (no son columnas visibles)
      ["_clipViewMore", "_wrapText"].forEach((meta) => {
        const temp = this.tempSelectedColumns && this.tempSelectedColumns[meta];
        if (temp && typeof temp === "object") {
          this.$set(
            this.selectedColumns,
            meta,
            JSON.parse(JSON.stringify(temp))
          );
        } else if (!this.selectedColumns[meta]) {
          this.$set(this.selectedColumns, meta, {});
        }
      });

      // Emitir el evento para que se guarden los cambios
      this.$emit("columns-changed");
      this.$emit("close");
    },
    cancelChanges() {
      // Restaurar las columnas originales
      this.tempSelectedColumns = JSON.parse(
        JSON.stringify(this.selectedColumns)
      );
      // Restaurar las sumas originales
      if (this.selectedColumns._columnSums) {
        this.tempColumnSums = JSON.parse(
          JSON.stringify(this.selectedColumns._columnSums)
        );
      } else {
        this.tempColumnSums = {};
      }
      this.$emit("close");
    },
    handleToggleAllColumns(event) {
      if (event.target.checked) {
        this.selectAllColumns();
      } else {
        this.deselectAllColumns();
      }
    },
    selectAllColumns() {
      // Seleccionar todos los campos normales (excluyendo campos ocultos)
      this.nonGroupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, true);
      });

      // Función recursiva para seleccionar campos anidados
      const selectAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay) {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, true);
            
            // Si es un grupo, también seleccionar sus campos
            if (subField.type === 'group' && subField.fields) {
              selectAllNested(subField.fields, fieldPath);
            }
          }
        });
      };

      // Seleccionar todos los campos de grupo y sus subcampos recursivamente
      this.groupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, true);
        selectAllNested(field.fields || [], field.key);
      });

      // Forzar la actualización de la UI
      this.$forceUpdate();
    },
    deselectAllColumns() {
      // Deseleccionar todos los campos normales (excluyendo campos ocultos)
      this.nonGroupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, false);
      });

      // Función recursiva para deseleccionar campos anidados
      const deselectAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay) {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, false);
            
            // Si es un grupo, también deseleccionar sus campos
            if (subField.type === 'group' && subField.fields) {
              deselectAllNested(subField.fields, fieldPath);
            }
          }
        });
      };

      // Deseleccionar todos los campos de grupo y sus subcampos recursivamente
      this.groupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, false);
        deselectAllNested(field.fields || [], field.key);
      });

      // Forzar la actualización de la UI
      this.$forceUpdate();
    },
    toggleGroupFields(field, basePath) {
      const isSelected = this.tempSelectedColumns[basePath];
      
      // Función recursiva para toggle de todos los campos anidados
      const toggleAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
        if (!subField.hideDisplay) {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, isSelected);
            
            // Si es un grupo anidado, también toggle sus campos
            if (subField.type === 'group' && subField.fields) {
              toggleAllNested(subField.fields, fieldPath);
            }
          }
        });
      };
      
      toggleAllNested(field.fields || [], basePath);
    },
    
    toggleNestedGroupFields(nestedGroup, basePath, parentField) {
      const isSelected = this.tempSelectedColumns[basePath];
      
      // Toggle todos los campos dentro del grupo anidado
      const toggleAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay) {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, isSelected);
            
            // Si es un grupo anidado, también toggle sus campos
            if (subField.type === 'group' && subField.fields) {
              toggleAllNested(subField.fields, fieldPath);
            }
          }
        });
      };
      
      toggleAllNested(nestedGroup.fields || [], basePath);
      
      // Actualizar selección del grupo padre
      this.updateGroupSelection(parentField);
    },
    
    toggleDeepNestedGroupFields(deepGroup, basePath, parentGroup, rootField) {
      const isSelected = this.tempSelectedColumns[basePath];
      
      // Toggle todos los campos dentro del grupo profundo
      (deepGroup.fields || []).forEach((subField) => {
        if (!subField.hideDisplay) {
          this.$set(this.tempSelectedColumns, `${basePath}.${subField.key}`, isSelected);
        }
      });
      
      // Actualizar selección de grupos padres
      const parentPath = basePath.split('.').slice(0, -1).join('.');
      this.updateNestedGroupSelection(parentGroup, parentPath, rootField);
    },
    
    updateGroupSelection(field) {
      // Verificar si algún campo directo o anidado está seleccionado
      const hasSelectedField = (fields, parentPath) => {
        return fields.some((subField) => {
          if (subField.hideDisplay) return false;
          const fieldPath = `${parentPath}.${subField.key}`;
          
          if (this.tempSelectedColumns[fieldPath] === true) {
            return true;
          }
          
          // Verificar campos anidados si es un grupo
          if (subField.type === 'group' && subField.fields) {
            return hasSelectedField(subField.fields, fieldPath);
          }
          
          return false;
        });
      };
      
      const someSelected = hasSelectedField(field.fields || [], field.key);
      this.$set(this.tempSelectedColumns, field.key, someSelected);
    },
    
    updateNestedGroupSelection(nestedGroup, nestedPath, parentField) {
      // Verificar si algún campo del grupo anidado está seleccionado
      const hasSelectedField = (fields, parentPath) => {
        return fields.some((subField) => {
          if (subField.hideDisplay) return false;
          const fieldPath = `${parentPath}.${subField.key}`;
          
          if (this.tempSelectedColumns[fieldPath] === true) {
            return true;
          }
          
          // Verificar campos anidados si es un grupo
          if (subField.type === 'group' && subField.fields) {
            return hasSelectedField(subField.fields, fieldPath);
          }
          
          return false;
        });
      };
      
      const someSelected = hasSelectedField(nestedGroup.fields || [], nestedPath);
      this.$set(this.tempSelectedColumns, nestedPath, someSelected);
      
      // También actualizar el grupo padre
      this.updateGroupSelection(parentField);
    },
    
    updateDeepGroupSelection(deepGroup, deepPath, parentGroup, parentPath, rootField) {
      // Verificar si algún campo del grupo profundo está seleccionado
      const visibleFields = (deepGroup.fields || []).filter(f => !f.hideDisplay);
      const someSelected = visibleFields.some(
        (subField) => this.tempSelectedColumns[`${deepPath}.${subField.key}`] === true
      );
      
      this.$set(this.tempSelectedColumns, deepPath, someSelected);
      
      // Actualizar grupos padres
      this.updateNestedGroupSelection(parentGroup, parentPath, rootField);
    },
    
    async toggleReferenceExpand(field) {
      const isExpanded = this.expandedReferences[field.key];
      this.$set(this.expandedReferences, field.key, !isExpanded);
      
      // Si se está expandiendo y no hay campos cargados, cargarlos
      if (!isExpanded && !this.referenceSubFields[field.key]) {
        await this.loadReferenceFields(field);
      }
    },
    
    async loadReferenceFields(field) {
      if (!field.ref) return;
      
      this.$set(this.loadingReferenceFields, field.key, true);
      
      try {
        // Cargar la definición del modelo referenciado
        const response = await wencesApi.get(`/api/gotrading/model-definitions/${field.ref}`);
        
        if (response.data && response.data.fields) {
          // Filtrar solo campos que se pueden mostrar en columnas
          const displayableFields = response.data.fields
            .filter(f => 
              !f.hideDisplay && 
              f.type !== 'group' && 
              ['string', 'number', 'date', 'datetime', 'boolean', 'list', 'reference', 'users', 'text', 'email', 'phone', 'autoincrement'].includes(f.type)
            )
            .sort((a, b) => {
              const labelA = (a.label || a.key).toLowerCase();
              const labelB = (b.label || b.key).toLowerCase();
              return labelA.localeCompare(labelB);
            });
          
          this.$set(this.referenceSubFields, field.key, withReferenceSystemSubFields(displayableFields));
        } else {
          this.$set(this.referenceSubFields, field.key, []);
        }
      } catch (error) {
        console.error(`Error loading reference fields for ${field.ref}:`, error);
        this.$set(this.referenceSubFields, field.key, []);
      } finally {
        this.$set(this.loadingReferenceFields, field.key, false);
      }
    },
    isNumericField(fieldType) {
      return ['number', 'currency'].includes(fieldType);
    },
    isArrayGroup(f) {
      return (
        f &&
        f.type === "group" &&
        (f.isArray === true || f.isArray === "true")
      );
    },
    garRefStorageKey(field, subField, nestedField, deepField) {
      const keys = [field.key, subField.key];
      if (nestedField) keys.push(nestedField.key);
      if (deepField) keys.push(deepField.key);
      return keys.join(".");
    },
    async toggleGarRefExpand(refField, storageKey) {
      const isExpanded = this.expandedGarRefs[storageKey];
      this.$set(this.expandedGarRefs, storageKey, !isExpanded);
      if (!isExpanded && !this.garRefSubFields[storageKey]) {
        await this.loadGarRefSubFields(refField, storageKey);
      }
    },
    async loadGarRefSubFields(refField, storageKey) {
      if (!refField?.ref) return;
      this.$set(this.loadingGarRefFields, storageKey, true);
      try {
        const response = await wencesApi.get(
          `/api/gotrading/model-definitions/${refField.ref}`
        );
        if (response.data && response.data.fields) {
          const displayableFields = response.data.fields
            .filter(
              (f) =>
                !f.hideDisplay &&
                f.type !== "group" &&
                [
                  "string",
                  "number",
                  "date",
                  "datetime",
                  "boolean",
                  "list",
                  "reference",
                  "users",
                  "text",
                  "email",
                  "phone",
                  "autoincrement",
                ].includes(f.type)
            )
            .sort((a, b) => {
              const labelA = (a.label || a.key).toLowerCase();
              const labelB = (b.label || b.key).toLowerCase();
              return labelA.localeCompare(labelB);
            });
          this.$set(this.garRefSubFields, storageKey, withReferenceSystemSubFields(displayableFields));
        } else {
          this.$set(this.garRefSubFields, storageKey, []);
        }
      } catch (e) {
        console.error(`Error loading GAR reference fields for ${refField.ref}:`, e);
        this.$set(this.garRefSubFields, storageKey, []);
      } finally {
        this.$set(this.loadingGarRefFields, storageKey, false);
      }
    },
    getColumnSum(columnKey) {
      if (!this.tempColumnSums || !columnKey) {
        return false;
      }
      return this.tempColumnSums[columnKey] === true;
    },
    toggleColumnSum(columnKey, value) {
      if (!this.tempColumnSums) {
        this.tempColumnSums = {};
      }
      this.$set(this.tempColumnSums, columnKey, value);
    },
  },
};
</script>

<style scoped>
.column-selector-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
}

.popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
}

.popup-content {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 350px;
  max-width: calc(100vw - 40px);
  transition: width 0.2s ease;
  max-height: 80vh;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.popup-content.popup-width-sm {
  width: 480px;
}

.popup-content.popup-width-md {
  width: 600px;
}

.popup-content.popup-width-lg {
  width: 720px;
}

.popup-content.popup-width-xl {
  width: 860px;
}

.popup-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.popup-header h6 {
  color: #495057;
  font-weight: 600;
}

.search-box {
  position: relative;
}

.search-box .search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.8rem;
}

.search-box .form-control {
  padding-left: 30px;
  border-color: #dee2e6;
}

.select-all-label {
  color: #495057;
  cursor: pointer;
  font-size: 0.875rem;
  margin: 0;
  font-weight: 500;
}

.form-check-input:checked {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.form-check-input:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.count-badge {
  background-color: #6c757d;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
}

.btn-close {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
}

.btn-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.popup-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 200px;
  max-height: calc(80vh - 170px);
}

.column-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.column-item:last-child {
  border-bottom: none;
}

.column-toggle {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.column-toggle .form-check-input {
  margin-right: 8px;
}

.column-toggle .form-check-label {
  cursor: pointer;
  margin: 0;
  font-size: 0.875rem;
  color: #495057;
  min-width: 0;
  overflow-wrap: anywhere;
}

.column-handle {
  color: #adb5bd;
  cursor: grab;
  padding: 4px;
}

.column-handle:hover {
  color: #6c757d;
}

.group-section {
  margin-bottom: 16px;
}

.sub-columns {
  margin-left: 20px;
  margin-top: 8px;
}

.sub-item {
  padding: 6px 0;
}

.sub-item .column-toggle .form-check-label {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Estilos para grupos anidados de 2do nivel */
.nested-group-section {
  margin-top: 4px;
  margin-bottom: 8px;
  border-left: 2px solid #28a745;
  padding-left: 8px;
}

.nested-group-header {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 4px 8px !important;
}

.nested-group-header .form-check-label {
  font-size: 0.8rem !important;
  color: #495057 !important;
}

.nested-level-2 {
  margin-left: 12px;
  border-left: 1px dashed #dee2e6;
  padding-left: 8px;
}

.nested-level-2 .column-item {
  padding: 4px 0;
}

.nested-level-2 .form-check-label {
  font-size: 0.8rem !important;
  color: #495057 !important;
}

/* Estilos para grupos anidados de 3er nivel */
.nested-group-section.level-3 {
  border-left-color: #ffc107;
}

.nested-level-3 {
  margin-left: 12px;
  border-left: 1px dotted #ffc107;
  padding-left: 8px;
}

.nested-level-3 .column-item {
  padding: 3px 0;
}

.nested-level-3 .form-check-label {
  font-size: 0.78rem !important;
  color: #5a6268 !important;
}

.nested-level-3-field-wrap {
  display: contents;
}

.badge-primary {
  background-color: #28a745;
  color: white;
}

/* Estilos para campos reference */
.reference-section {
  border-left: 3px solid #17a2b8;
  padding-left: 8px;
  margin-left: -8px;
}

.reference-sub-columns {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 8px;
  margin-top: 4px;
}

.column-actions {
  display: flex;
  align-items: center;
}

.btn-expand {
  background: none;
  border: 1px solid #dee2e6;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  transition: all 0.2s;
}

.btn-expand:hover {
  background-color: #e9ecef;
  color: #495057;
  border-color: #adb5bd;
}

.loading-indicator {
  color: #6c757d;
  font-size: 0.8rem;
  padding: 8px;
  text-align: center;
}

.no-fields-message {
  color: #adb5bd;
  font-size: 0.8rem;
  font-style: italic;
  padding: 8px;
  text-align: center;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.popup-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.popup-footer .btn {
  flex: 1;
}

.btn-outline-secondary {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-secondary:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-primary {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.btn-primary:hover {
  background-color: #138496;
  border-color: #138496;
}

/* Scrollbar personalizado */
.popup-body::-webkit-scrollbar {
  width: 6px;
}

.popup-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.popup-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.popup-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
